import { useCallback, useEffect, useRef, useState } from 'react';

import { CHAR_WIDTH_PX, MINIMUM_TICK_WIDTH_STRAIGHT } from '../utils/chartUtils';
import { MetricDatum } from '../types';

interface UseAutoRotateLabelsOptions {
  data: MetricDatum[];
  xAxisKeys: string[];
  yAxisWidth: number;
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  isHorizontal?: boolean;
  angleLabels?: boolean;
  showFullLabels?: boolean;
}

/**
 * Custom hook that automatically determines whether chart labels should be rotated
 * based on available space. Uses ResizeObserver to track container width and calculates
 * the optimal rotation state to prevent labels from being hidden by Recharts.
 *
 * @param options Configuration options for label rotation calculation
 * @returns Object containing containerRef (to attach to container), effectiveAngleLabels (calculated rotation state)
 */
export const useAutoRotateLabels = ({
  data,
  xAxisKeys,
  yAxisWidth,
  hideXAxis = false,
  hideYAxis = false,
  isHorizontal = false,
  angleLabels = false,
  showFullLabels = false,
}: UseAutoRotateLabelsOptions) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Observe container width changes for auto-rotation calculation
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setContainerWidth(width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * Auto-calculate whether labels should be rotated based on available space.
   * Note: Horizontal charts never rotate labels since Y-axis labels have vertical space.
   */
  const shouldRotateLabels = useCallback(() => {
    // Horizontal charts should never rotate labels - they have vertical space on Y-axis
    if (isHorizontal) {
      return false;
    }

    // Always respect explicit angleLabels prop if container width isn't available yet
    if (!containerWidth) {
      return angleLabels;
    }

    // Calculate available width per bar
    const numberOfBars = data.length;
    if (numberOfBars === 0) {
      return false;
    }

    // Account for margins and Y-axis width
    const chartMargins = hideXAxis && hideYAxis ? 20 : yAxisWidth + 25;
    const availableWidth = containerWidth - chartMargins;
    const widthPerBar = availableWidth / numberOfBars;

    // Calculate the average and max label lengths in the dataset
    const xAxisKey = xAxisKeys[0];
    const labelLengths = data.map((d) => {
      const label = d[xAxisKey]?.toString() || '';
      return showFullLabels ? label.length : Math.min(label.length, 20);
    });

    const avgLabelLength = labelLengths.reduce((sum, len) => sum + len, 0) / labelLengths.length;
    const maxLabelLength = Math.max(...labelLengths);

    // Use weighted average: 70% average length, 30% max length
    // This prevents premature rotation from one long outlier
    const effectiveLabelLength = avgLabelLength * 0.7 + maxLabelLength * 0.3;
    const estimatedLabelWidth = effectiveLabelLength * CHAR_WIDTH_PX;

    // Recharts needs minimum spacing between labels
    // Use MINIMUM_TICK_WIDTH_STRAIGHT as the absolute minimum
    const minRequiredWidth = Math.max(MINIMUM_TICK_WIDTH_STRAIGHT, estimatedLabelWidth);

    // Rotate if we don't have enough space per bar
    // This prevents Recharts from hiding labels before we rotate
    return widthPerBar < minRequiredWidth;
  }, [containerWidth, data, xAxisKeys, showFullLabels, angleLabels, isHorizontal, hideXAxis, hideYAxis, yAxisWidth]);

  // Use calculated rotation value
  const effectiveAngleLabels = shouldRotateLabels();

  return {
    containerRef,
    effectiveAngleLabels,
  };
};
